import React, { useContext, useMemo } from 'react';
import { UserContext } from '../contexts/UserContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { Storefront, Person, Home, Pets, Search, SupportAgent, LocalShipping } from '@mui/icons-material';
import { Box, ButtonBase, useTheme } from '@mui/material';
import { layoutPillRadius, lead800, navBarInactiveCircle, navBarSurface, white } from '../theme/colors';

const INACTIVE_SIZE = 44;
const ACTIVE_LABEL_MAX_PX = 200;

function NavItem({ label, pathSegment, icon: Icon, active, onNavigate, theme }) {
  const ease = theme.transitions.easing.easeOut;
  const duration = theme.transitions.duration.short;
  const t = theme.transitions.create(
    ['min-width', 'width', 'padding', 'background-color', 'border-radius', 'gap', 'transform', 'box-shadow'],
    { duration, easing: ease },
  );
  const labelT = theme.transitions.create(['max-width', 'opacity'], { duration, easing: ease });
  const iconT = theme.transitions.create(['transform'], { duration: theme.transitions.duration.shorter, easing: ease });

  return (
    <ButtonBase
      focusRipple
      aria-current={active ? 'page' : undefined}
      onClick={() => onNavigate(pathSegment)}
      sx={{
        flexShrink: 0,
        height: INACTIVE_SIZE,
        minHeight: INACTIVE_SIZE,
        px: active ? 1.25 : 0,
        py: 0,
        gap: active ? 0.75 : 0,
        borderRadius: active ? layoutPillRadius : '50%',
        width: active ? 'auto' : INACTIVE_SIZE,
        minWidth: INACTIVE_SIZE,
        maxWidth: active ? 280 : INACTIVE_SIZE,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: active ? 'primary.main' : navBarInactiveCircle,
        color: white,
        transition: t,
        overflow: 'hidden',
        transform: 'scale(1)',
        '&:hover': {
          bgcolor: active ? 'primary.main' : lead800,
        },
        '&:active': {
          transform: 'scale(0.96)',
          transitionDuration: `${theme.transitions.duration.shorter}ms`,
        },
        '& .MuiSvgIcon-root': {
          fontSize: 22,
          color: 'inherit',
          flexShrink: 0,
          transition: iconT,
        }
      }}
    >
      <Icon />
      <Box
        component="span"
        sx={{
          maxWidth: active ? ACTIVE_LABEL_MAX_PX : 0,
          opacity: active ? 1 : 0,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          typography: 'caption',
          fontWeight: 700,
          color: 'inherit',
          transition: labelT,
          fontSize: '16px',
        }}
      >
        {label}
      </Box>
    </ButtonBase>
  );
}

function Navbar() {
  const { currentUser } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const activeLink = (segment) => {
    const base = `/${segment}`;
    const { pathname } = location;
    return pathname === base || pathname.startsWith(`${base}/`);
  };

  const items = useMemo(() => {
    const list = [
      { key: 'home', label: 'Home', segment: 'home', Icon: Home },
      { key: 'search', label: 'Buscar', segment: 'search', Icon: Search },
      { key: 'mypets', label: 'Meus Pets', segment: 'mypets', Icon: Pets },
    ];
    if (currentUser?.courier_id) {
      list.push({
        key: 'delivery',
        label: 'Entregador',
        segment: 'delivery-dashboard',
        Icon: LocalShipping,
      });
    }
    if (currentUser?.provider_id) {
      list.push({
        key: 'provider',
        label: 'Fornecedor',
        segment: 'provider-dashboard',
        Icon: Storefront,
      });
    }
    if (currentUser?.role === 'admin') {
      list.push({
        key: 'control-center',
        label: 'Centro de Controle',
        segment: 'control-center',
        Icon: SupportAgent,
      });
    }
    list.push({ key: 'profile', label: 'Perfil', segment: 'profile', Icon: Person });
    return list;
  }, [currentUser]);

  const onNavigate = (segment) => {
    navigate(`/${segment}`);
  };

  return (
    <Box sx={{ pt: 8 }}>
      <Box
        sx={{
          position: 'fixed',
          bottom: { xs: 12, sm: 16 },
          left: { xs: 12, sm: 16 },
          right: { xs: 12, sm: 16 },
          zIndex: 9999,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <Box
          sx={(theme) => ({
            pointerEvents: 'auto',
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            alignItems: 'center',
            gap: 0.75,
            py: 0.75,
            px: 0.75,
            maxWidth: '100%',
            borderRadius: layoutPillRadius,
            bgcolor: navBarSurface,
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.18)',
            transform: 'translateY(0)',
            transition: theme.transitions.create(['box-shadow', 'transform'], {
              duration: theme.transitions.duration.standard,
              easing: theme.transitions.easing.easeOut,
            }),
            animation: 'navbarDockIn 0.42s cubic-bezier(0.34, 1.2, 0.64, 1) both',
            '@keyframes navbarDockIn': {
              from: {
                opacity: 0,
                transform: 'translateY(16px)',
              },
              to: {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
            '&:hover': {
              boxShadow: '0 8px 28px rgba(0, 0, 0, 0.24)',
            },
            '&::-webkit-scrollbar': { display: 'none' },
          })}
        >
          {items.map(({ key, label, segment, Icon }) => (
            <NavItem
              key={key}
              label={label}
              pathSegment={segment}
              icon={Icon}
              active={activeLink(segment)}
              onNavigate={onNavigate}
              theme={theme}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default Navbar;
